import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import CancelIcon from "@mui/icons-material/Cancel";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import axios from "axios";
import moment from "moment";
import { LoadingButton } from "@material-ui/lab";

// Shipshopy chat — visible only to SCM + the two external Shipshopy users (see
// deliveryCenterExternalAccess.js). Copy of DeliveryCenterChatDrawer.js pointed at the
// shipshopyChatMessages thread instead of deliveryCenterChatMessages, senderRole "SCM"/"EXTERNAL"
// instead of "SCM"/"AGENT_MANAGER". No reply-validity marking here — wasn't requested for this
// chat. Sending is never disabled by the Harshit account lock — a locked external user must
// still be able to reply (enforced server-side too, see sendShipshopyChatMessage).

function bodyTypeForFile(file) {
  if (!file || !file.type) return "file";
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("audio/")) return "audio";
  return "file";
}

function formatDaySeparator(date) {
  const m = moment(date);
  if (m.isSame(moment(), "day")) return "Today";
  if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  return m.format("DD MMM YYYY");
}

function formatAddress(address) {
  if (!address) return "";
  const parts = [address.address, address.landmark, address.city, address.state, address.pincode].filter(Boolean);
  return parts.join(", ");
}

function MessageBubble({ message, isRight }) {
  const readers = (message.readBy || []).filter((r) => r.name && r.name !== message.senderName);
  return (
    <Box sx={{ display: "flex", justifyContent: isRight ? "flex-end" : "flex-start", mb: 1.5 }}>
      <Box
        sx={{
          maxWidth: "70%",
          backgroundColor: isRight ? "#dcf8c6" : "#ffffff",
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          p: 1,
          position: "relative",
          boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
        }}
      >
        <Typography variant="caption" sx={{ display: "block", textAlign: "right", fontWeight: 600, color: "#555" }}>
          {message.senderName || (message.senderRole === "SCM" ? "SCM" : "Shipshopy")}
        </Typography>
        {message.bodyType === "image" && message.mediaUrl && (
          <Box sx={{ mt: 0.5, mb: message.text ? 0.5 : 0 }}>
            <img
              src={message.mediaUrl}
              alt="attachment"
              style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 6, cursor: "pointer" }}
              onClick={() => window.open(message.mediaUrl, "_blank")}
            />
          </Box>
        )}
        {message.bodyType === "video" && message.mediaUrl && (
          <Box sx={{ mt: 0.5, mb: message.text ? 0.5 : 0 }}>
            <video src={message.mediaUrl} controls style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 6 }} />
          </Box>
        )}
        {message.bodyType === "audio" && message.mediaUrl && (
          <Box sx={{ mt: 0.5, mb: message.text ? 0.5 : 0 }}>
            <audio src={message.mediaUrl} controls style={{ maxWidth: "100%" }} />
          </Box>
        )}
        {message.bodyType === "file" && message.mediaUrl && (
          <Box
            component="a"
            href={message.mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              mt: 0.5,
              mb: message.text ? 0.5 : 0,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              p: 0.75,
              backgroundColor: "#f0f0f0",
              borderRadius: 1,
              textDecoration: "none",
              color: "#075e54",
            }}
          >
            <InsertDriveFileIcon fontSize="small" />
            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
              {message.mediaName || "Attachment"}
            </Typography>
          </Box>
        )}
        {message.text && <Typography variant="body2">{message.text}</Typography>}
        <Typography variant="caption" sx={{ display: "block", color: "#888", mt: 0.5 }}>
          {moment(message.createdAt).format("hh:mm A")}
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "#888" }}>
          {readers.length ? `Read by ${readers.map((r) => r.name).join(", ")}` : "Sent"}
        </Typography>
      </Box>
    </Box>
  );
}

function ShipshopyChatDrawer({ open, orderId, onClose }) {
  const [orderInfo, setOrderInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [attachedFile, setAttachedFile] = useState(null);
  const [attachedPreview, setAttachedPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);

  const loadChat = () => {
    if (!orderId) return;
    setLoading(true);
    axios
      .post(`${process.env.REACT_APP_PATH_BULK}/sales/getShipshopyChat`, { orderId })
      .then((res) => {
        setLoading(false);
        if (res.data.success) {
          setOrderInfo(res.data.data?.order || null);
          setMessages(res.data.data?.messages || []);
        } else {
          alert(res.data.message);
        }
      })
      .catch((err) => {
        setLoading(false);
        alert(err?.response?.data?.message || "something went wrong");
      });
  };

  useEffect(() => {
    if (open && orderId) {
      loadChat();
    } else {
      setOrderInfo(null);
      setMessages([]);
      setText("");
      setAttachedFile(null);
      setAttachedPreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachedFile(file);
    setAttachedPreview(file.type.startsWith("image/") ? URL.createObjectURL(file) : null);
  };

  const removeAttachment = () => {
    setAttachedFile(null);
    setAttachedPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSend = async () => {
    if (!text.trim() && !attachedFile) return;
    setSending(true);
    try {
      let mediaUrl = "";
      let bodyType = "text";
      let mediaName = "";
      if (attachedFile) {
        bodyType = bodyTypeForFile(attachedFile);
        mediaName = attachedFile.name;
        const form = new FormData();
        form.append("image", attachedFile);
        const uploadRes = await axios.post(
          `${process.env.REACT_APP_PATH_BULK}/sales/uploadDeliveryCenterChatImage`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (!uploadRes.data.success) {
          alert(uploadRes.data.message || "attachment upload failed");
          setSending(false);
          return;
        }
        mediaUrl = uploadRes.data.data?.url || uploadRes.data.message?.url || "";
      }
      const res = await axios.post(`${process.env.REACT_APP_PATH_BULK}/sales/sendShipshopyChatMessage`, {
        orderId,
        text: text.trim(),
        mediaUrl,
        bodyType,
        mediaName,
      });
      setSending(false);
      if (res.data.success) {
        setText("");
        removeAttachment();
        loadChat();
      } else {
        alert(res.data.message);
      }
    } catch (err) {
      setSending(false);
      alert(err?.response?.data?.message || "something went wrong");
    }
  };

  let lastDay = null;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0", backgroundColor: "#f5f5f5" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={600}>
              {orderInfo?.customerName || "Shipshopy Chat"}
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Invoice: {orderInfo?.invoiceNumber || "-"} | Phone: {orderInfo?.phoneNumber || "-"}
          </Typography>
          {orderInfo?.address && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {formatAddress(orderInfo.address)}
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", p: 2, backgroundColor: "#ece5dd" }}>
          {orderInfo?.orderCreatedAt && (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Box sx={{ backgroundColor: "#fff", borderRadius: 2, px: 1.5, py: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Order placed on {moment(orderInfo.orderCreatedAt).format("DD MMM YYYY, hh:mm A")}
                </Typography>
              </Box>
            </Box>
          )}
          {messages.map((message) => {
            const day = moment(message.createdAt).format("YYYY-MM-DD");
            const showSeparator = day !== lastDay;
            lastDay = day;
            const isRight = message.senderRole === "EXTERNAL";
            return (
              <React.Fragment key={message._id || `${message.senderId}-${message.createdAt}`}>
                {showSeparator && (
                  <Box sx={{ display: "flex", justifyContent: "center", my: 1.5 }}>
                    <Box sx={{ backgroundColor: "#fff", borderRadius: 2, px: 1.5, py: 0.3 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDaySeparator(message.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                )}
                <MessageBubble message={message} isRight={isRight} />
              </React.Fragment>
            );
          })}
          {!loading && messages.length === 0 && (
            <Typography variant="body2" color="text.secondary" align="center">
              No messages yet.
            </Typography>
          )}
          <div ref={bottomRef} />
        </Box>

        <Box sx={{ p: 1.5, borderTop: "1px solid #e0e0e0" }}>
          {attachedFile && (
            <Box sx={{ position: "relative", display: "inline-block", mb: 1 }}>
              {attachedPreview ? (
                <img src={attachedPreview} alt="preview" style={{ maxHeight: 100, borderRadius: 6 }} />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    p: 1,
                    pr: 3,
                    backgroundColor: "#f0f0f0",
                    borderRadius: 1,
                    maxWidth: 260,
                  }}
                >
                  <InsertDriveFileIcon fontSize="small" />
                  <Typography variant="caption" noWrap>
                    {attachedFile.name}
                  </Typography>
                </Box>
              )}
              <IconButton size="small" onClick={removeAttachment} sx={{ position: "absolute", top: -10, right: -10 }}>
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            <input
              type="file"
              accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
            <IconButton onClick={() => fileInputRef.current?.click()}>
              <AttachFileIcon />
            </IconButton>
            <TextField
              size="small"
              fullWidth
              placeholder="Type a message"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <LoadingButton variant="contained" loading={sending} onClick={handleSend}>
              <SendIcon fontSize="small" />
            </LoadingButton>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
}

export default ShipshopyChatDrawer;
